import { PackageServiceItems } from '@components/detailed-pricing/PackageServiceItems';
import {
	PackageModalProvider,
	usePackageModalContext,
} from '@components/detailed-pricing/contexts/PackageModalContext';
import {
	useFormattedPriceType,
	useModal,
	usePackagePrice,
} from '@components/detailed-pricing/hooks/use-package-venue';
import RfqModal from '@tkmp/rfq/components/RfqModal/RfqModal';
import { useInlineRfqForm } from '@tkmp/rfq/hooks/useInlineRfqForm/useInlineRfqForm';
import RFQButton from '@tkmp/rfq/shared/cta_box/RfqButton';
import { Card } from '@tkww/orion-web-ds-card';
import { SidebarModal, SidebarModalPane } from '@tkww/orion-web-ds-dialog';
import { StatusBadge } from '@tkww/orion-web-ds-feedback';
import { Icon } from '@tkww/orion-web-ds-graphic';
import { TextBody, TextCaption, TextCategory } from '@tkww/orion-web-ds-text';
import { compose } from '@xo-union/react-css-modules';
import React, { useState } from 'react';
import type {
	DetailedPricingPackage,
	Periods,
} from '../../jotai/storefront/detailed-pricing';
import Styles from './packageVenue.scss';

const PriceSection = ({ pkg }: { pkg: DetailedPricingPackage }) => {
	const [activeTab, setActiveTab] = useState<Periods>('peak');
	const formattedPriceType = useFormattedPriceType(pkg.priceType ?? '');
	const price = usePackagePrice(pkg, activeTab);

	return (
		<>
			<div className={Styles.packagePrice}>
				<TextCategory bold category="subhead" className={Styles.price}>
					{price}
				</TextCategory>
				<TextBody size="sm">{formattedPriceType}</TextBody>
			</div>
			{pkg.priceSeasonality === 'seasonal' && (
				<div className={Styles.priceSeason}>
					{(['peak', 'off-peak'] as const).map((tab) => (
						<TextCaption
							key={tab}
							as="button"
							onClick={() => setActiveTab(tab)}
							className={activeTab === tab ? Styles.activeTab : ''}
						>
							{tab === 'peak' ? 'Peak season' : 'Off-peak season'}
						</TextCaption>
					))}
				</div>
			)}
		</>
	);
};

const PackageHeader = ({
	pkg,
	showToggle = true,
}: { pkg: DetailedPricingPackage; showToggle?: boolean }) => {
	const { isCollapsed, collapseDetails } = usePackageModalContext();

	return (
		<div className={Styles.heading}>
			<TextCaption>{pkg.packageType}</TextCaption>
			<TextBody size="md" bold>
				{pkg.title}
			</TextBody>
			<PriceSection pkg={pkg} />
			{showToggle && (
				<button
					type="button"
					className={Styles.collapse}
					onClick={() => collapseDetails(!isCollapsed)}
				>
					<Icon
						name={!isCollapsed ? 'caret-up' : 'caret-down'}
						size="md"
						aria-label={!isCollapsed ? 'Collapse details' : 'Expand details'}
					/>
				</button>
			)}
		</div>
	);
};

const PackageServiceList = ({
	pkg,
	showLimited,
}: {
	pkg: DetailedPricingPackage;
	showLimited: boolean;
}) => {
	const { isCollapsed } = usePackageModalContext();

	return (
		<div className={isCollapsed ? Styles.collapsed : ''}>
			<hr className={Styles.divider} />
			<ul>
				<PackageServiceItems
					pkg={pkg}
					testId="expand-service-items"
					showLimited={showLimited}
				/>
			</ul>
		</div>
	);
};

const PackageDetailsModal = ({ pkg }: { pkg: DetailedPricingPackage }) => {
	const rfqContext = useInlineRfqForm({
		initiator: 'Get a Personalized Quote CTA',
	});
	const { isOpen, closeModal } = usePackageModalContext();

	return (
		<SidebarModal
			isOpen={isOpen && !rfqContext.isModalOpen}
			onClose={closeModal}
		>
			<SidebarModalPane
				header=""
				classes={compose({
					'header-container': Styles.sidebarHeader,
					footer: Styles.sidebarFooter,
				})}
				secondaryAction={() => (
					<>
						<RfqModal context={rfqContext} />
						<RFQButton
							type="message"
							color="primary"
							size="lg"
							styleName={Styles.sidebarCtaButton}
							label="Request quote"
							handleCTAClick={rfqContext.openModal}
							testId="package-details-request-quote"
						/>
					</>
				)}
			>
				<div className={Styles.sidebarContent}>
					<PackageHeader pkg={pkg} showToggle={false} />
					<PackageServiceList pkg={pkg} showLimited={false} />
				</div>
			</SidebarModalPane>
		</SidebarModal>
	);
};

const PackageContent = ({ pkg }: { pkg: DetailedPricingPackage }) => {
	const [collapseDetails, setCollapseDetails] = useState(false);
	const { isOpenModal, open: openModal, close: closeModal } = useModal();

	return (
		<PackageModalProvider
			openModal={openModal}
			closeModal={closeModal}
			isOpen={isOpenModal}
			isCollapsed={collapseDetails}
			collapseDetails={setCollapseDetails}
		>
			<PackageHeader pkg={pkg} />
			<PackageServiceList pkg={pkg} showLimited={true} />
			<PackageDetailsModal pkg={pkg} />
		</PackageModalProvider>
	);
};

export interface PackageVenueProps {
	pkg: DetailedPricingPackage;
}

export const PackageVenue = ({ pkg }: PackageVenueProps) => (
	<div className={Styles.cardContainer}>
		<Card className={Styles.card}>
			{pkg.mostPopular && (
				<StatusBadge
					styleType="featured"
					size="md"
					classes={compose({ container: Styles.packageBadge })}
				>
					Most Popular
				</StatusBadge>
			)}
			<PackageContent pkg={pkg} />
		</Card>
	</div>
);
